import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Paper, Typography, TextField, FormControl, InputLabel, Select,
  MenuItem, FormControlLabel, Switch, Alert, CircularProgress, Chip, Avatar,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, FormHelperText
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { rawApiClient } from '../../services/api';
import {
  listAdminRepresentations,
  createRepresentation,
  updateRepresentation,
  deactivateRepresentation
} from '../../services/personaRepresentationApi';

const GENDER_OPTIONS = ['unspecified', 'female', 'male', 'neutral'];
const AGE_OPTIONS = ['unspecified', 'young_adult', 'adult', 'middle_aged', 'older'];
const AUDIENCE_OPTIONS = ['under_18', 'adult', 'mixed'];

const EMPTY_REP = {
  key: '',
  name: '',
  displayName: '',
  description: '',
  clientDescription: '',
  previewText: '',
  avatarId: '',
  voiceCatalogId: '',
  tags: { gender: 'unspecified', accent: '', ageRange: 'unspecified', language: 'en' },
  audienceTags: [],
  isActive: true,
  displayOrder: 0
};

/**
 * PersonaRepresentationManager — admin CRUD for the curated avatar+voice
 * bundles that overlay therapist personas. See spec 06.
 */
const PersonaRepresentationManager = ({ token }) => {
  const [reps, setReps] = useState([]);
  const [avatars, setAvatars] = useState([]);
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = creating
  const [form, setForm] = useState(EMPTY_REP);
  const [previewing, setPreviewing] = useState(null);

  const loadAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const [repsRes, voicesRes, avatarsRes] = await Promise.all([
        listAdminRepresentations(token, true),
        rawApiClient.get('/admin/voice-library/voices', { headers: { 'x-auth-token': token } }),
        rawApiClient.get('/admin/voice-library/avatars', { headers: { 'x-auth-token': token } })
      ]);
      setReps(Array.isArray(repsRes) ? repsRes : []);
      setVoices(voicesRes.data?.voices || []);
      setAvatars(avatarsRes.data?.avatars || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleOpenCreate = () => {
    setEditing(null);
    setForm(EMPTY_REP);
    setDialogOpen(true);
  };

  const handleOpenEdit = (rep) => {
    setEditing(rep);
    setForm({
      key: rep.key,
      name: rep.name,
      displayName: rep.displayName || '',
      description: rep.description || '',
      clientDescription: rep.clientDescription || '',
      previewText: rep.previewText || '',
      avatarId: rep.avatarId?._id || rep.avatarId || '',
      voiceCatalogId: rep.voiceCatalogId?._id || rep.voiceCatalogId || '',
      tags: {
        gender: rep.tags?.gender || 'unspecified',
        accent: rep.tags?.accent || '',
        ageRange: rep.tags?.ageRange || 'unspecified',
        language: rep.tags?.language || 'en'
      },
      audienceTags: Array.isArray(rep.audienceTags) ? rep.audienceTags : [],
      isActive: rep.isActive !== false,
      displayOrder: rep.displayOrder || 0
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    try {
      if (editing) {
        await updateRepresentation(token, editing._id, form);
        setSuccess('Representation updated');
      } else {
        await createRepresentation(token, form);
        setSuccess('Representation created');
      }
      setDialogOpen(false);
      await loadAll();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Save failed');
    }
  };

  const handleDeactivate = async (rep) => {
    if (!window.confirm(`Deactivate representation "${rep.name}"? It will no longer be available to clients.`)) return;
    try {
      await deactivateRepresentation(token, rep._id);
      setSuccess('Representation deactivated');
      await loadAll();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Deactivate failed');
    }
  };

  const playSample = (url) => {
    if (previewing) {
      previewing.pause();
      setPreviewing(null);
    }
    if (!url) return;
    const audio = new Audio(url);
    setPreviewing(audio);
    audio.play().catch(() => {});
    audio.onended = () => setPreviewing(null);
  };

  const toggleAudienceTag = (tag) => {
    setForm(f => {
      const has = f.audienceTags.includes(tag);
      return { ...f, audienceTags: has ? f.audienceTags.filter(t => t !== tag) : [...f.audienceTags, tag] };
    });
  };

  return (
    <Box sx={{ maxWidth: 1100, margin: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Persona Representations</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate} disabled={loading}>
          Add Representation
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Curated avatar + voice bundles that overlay therapist personas. Clients pick one (or accept the default).
        Personas, service groups, and therapists can each whitelist a subset.
        Under-18 audiences never see <code>photo_real</code> avatars regardless of these settings.
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : reps.length === 0 ? (
        <Typography color="text.secondary">No representations yet. Click <strong>Add Representation</strong> to create the first one.</Typography>
      ) : (
        <Paper variant="outlined" sx={{ p: 1 }}>
          {reps.map((rep) => {
            const voice = rep.voiceCatalogId;
            const sampleUrl = voice?.sampleUrl || null;
            return (
              <Box key={rep._id} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}>
                <Avatar src={rep.avatarImageUrl || undefined} sx={{ width: 56, height: 56 }}>{rep.name?.[0] || '?'}</Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                    {rep.name} {!rep.isActive && <Chip size="small" label="Inactive" color="default" sx={{ ml: 1 }} />}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    key: <code>{rep.key}</code> &middot; voice: {voice?.name || '—'} &middot; gender: {rep.tags?.gender || '—'} &middot; age: {rep.tags?.ageRange || '—'} &middot; accent: {rep.tags?.accent || '—'}
                  </Typography>
                  {rep.audienceTags?.length > 0 && (
                    <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {rep.audienceTags.map(t => <Chip key={t} size="small" label={t} variant="outlined" />)}
                    </Box>
                  )}
                </Box>
                <IconButton size="small" disabled={!sampleUrl} onClick={() => playSample(sampleUrl)} title="Play voice sample">
                  <PlayCircleOutlineIcon />
                </IconButton>
                <IconButton size="small" onClick={() => handleOpenEdit(rep)} title="Edit"><EditIcon /></IconButton>
                <IconButton size="small" disabled={!rep.isActive} onClick={() => handleDeactivate(rep)} title="Deactivate" color="error"><DeleteIcon /></IconButton>
              </Box>
            );
          })}
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? `Edit ${editing.name}` : 'Add Representation'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mt: 1 }}>
            <TextField
              label="Key"
              value={form.key}
              onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
              disabled={!!editing}
              helperText={editing ? 'Cannot change key after creation' : 'Stable identifier — lowercase, no spaces'}
              required
            />
            <TextField
              label="Name (admin)"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <TextField
              label="Display name (client)"
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              helperText="Falls back to admin name if blank"
            />
            <TextField
              label="Display order"
              type="number"
              value={form.displayOrder}
              onChange={(e) => setForm({ ...form, displayOrder: parseInt(e.target.value, 10) || 0 })}
            />
            <TextField
              label="Description (admin)"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              multiline
              rows={2}
              sx={{ gridColumn: '1 / -1' }}
            />
            <TextField
              label="Client description"
              value={form.clientDescription}
              onChange={(e) => setForm({ ...form, clientDescription: e.target.value })}
              helperText="Single line shown in the client picker, e.g., 'Dublin Irish, 30s'"
              sx={{ gridColumn: '1 / -1' }}
            />
            <TextField
              label="Preview text (optional override)"
              value={form.previewText}
              onChange={(e) => setForm({ ...form, previewText: e.target.value })}
              multiline
              rows={2}
              helperText="Spoken when previewing this persona's voice. Blank = voice/platform default. Tokens: {personaName}, {voiceName}, {clientDescription}."
              sx={{ gridColumn: '1 / -1' }}
            />
            <FormControl required>
              <InputLabel>Avatar</InputLabel>
              <Select
                label="Avatar"
                value={form.avatarId}
                onChange={(e) => setForm({ ...form, avatarId: e.target.value })}
              >
                {avatars.filter(a => a.isActive !== false).map(a => (
                  <MenuItem key={a._id} value={a._id}>{a.name} ({a.style})</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl required>
              <InputLabel>Voice</InputLabel>
              <Select
                label="Voice"
                value={form.voiceCatalogId}
                onChange={(e) => setForm({ ...form, voiceCatalogId: e.target.value })}
              >
                {voices.filter(v => v.isActive !== false).map(v => (
                  <MenuItem key={v._id} value={v._id}>{v.name} ({v.tags?.gender || '—'}{v.tags?.accent ? `, ${v.tags.accent}` : ''})</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel>Gender (observable)</InputLabel>
              <Select
                label="Gender (observable)"
                value={form.tags.gender}
                onChange={(e) => setForm({ ...form, tags: { ...form.tags, gender: e.target.value } })}
              >
                {GENDER_OPTIONS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel>Age range</InputLabel>
              <Select
                label="Age range"
                value={form.tags.ageRange}
                onChange={(e) => setForm({ ...form, tags: { ...form.tags, ageRange: e.target.value } })}
              >
                {AGE_OPTIONS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Accent"
              value={form.tags.accent}
              onChange={(e) => setForm({ ...form, tags: { ...form.tags, accent: e.target.value } })}
              helperText="e.g., 'Dublin Irish', 'Northern English'"
            />
            <TextField
              label="Language"
              value={form.tags.language}
              onChange={(e) => setForm({ ...form, tags: { ...form.tags, language: e.target.value } })}
            />
            <FormControl sx={{ gridColumn: '1 / -1' }}>
              <FormHelperText sx={{ ml: 0 }}>Audience tags — empty = visible to all</FormHelperText>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {AUDIENCE_OPTIONS.map(t => (
                  <Chip
                    key={t}
                    label={t}
                    color={form.audienceTags.includes(t) ? 'primary' : 'default'}
                    onClick={() => toggleAudienceTag(t)}
                  />
                ))}
              </Box>
            </FormControl>
            <FormControlLabel
              control={<Switch checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />}
              label="Active"
              sx={{ gridColumn: '1 / -1' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.key || !form.name || !form.avatarId || !form.voiceCatalogId}>
            {editing ? 'Save changes' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PersonaRepresentationManager;
